import React from "react";
import { Box, CardContent, Typography, Stack } from "@mui/material";
import SearchResultLink from "../../Search/components/SearchResultCard/SearchResultLink";
import { receiveType } from "../../../util/receiveTypeMapping";
import Card from "@mui/material/Card";
import { getTimestampInLocalTime } from "../../../util/dateUtil";
import { TransferCardProps } from "../../Search/types";
import { useStyles } from "../Transfer.styles";
import { useHistory } from "react-router-dom";
import { transferSourceSystem } from "../../../util/transferSourceSystem";
import CopyToClipboard from "../../CopyToClipboard/CopyToClipboard";

const TransferCard: React.FC<TransferCardProps> = ({
	receiveStatus,
	receiveDateCreated,
	receiveCreatedBy,
	displayMode = "TransferList",
	transfer,
}) => {
	const classes = useStyles();

	const history = useHistory();
	const {
		id,
		sourceId,
		status,
		sourceSystem,
		toLocation,
		type,
		fromLocation,
		requesterName,
		requesterId,
		reason,
		dateCreated,
		totalApprovedQuantity,
		totalInTransitQuantity,
		labelId,
		transferRequestId,
		consignmentCarrier,
		consignmentNumber,
	} = transfer;

	const onSelect = () => {
		history.push(`/transfers/${id}?t=${Date.now()}`);
	};

	return (
		<>
			<Card className={classes.transferCard} onClick={onSelect}>
				<CardContent sx={{ py: 1, px: 2 }}>
					<Stack spacing={0.5}>
						<Box>
							<Typography display="inline" variant="caption" color="textSecondary" component="div">
								Transfer Number
							</Typography>
							<Typography display="inline" variant="body2" component="div" className={classes.sourceId}>
								{sourceSystem.toLowerCase() === transferSourceSystem.Algo
									? sourceId
									: transferRequestId
									? transferRequestId
									: sourceId}
							</Typography>
						</Box>

						<Box display="flex" flexWrap="nowrap" gap={1}>
							<Box flex="0 0 50%" className={classes.transferItemCol1}>
								<Typography variant="caption" color="textSecondary" component="div">
									Transfer Status
								</Typography>
								<Typography variant="body2" component="div">
									{status}
								</Typography>
							</Box>
							<Box flex="0 0 50%" className={classes.transferItemCol2}>
								<Typography variant="caption" color="textSecondary" component="div">
									Source System
								</Typography>
								<Typography variant="body2" component="div">
									{sourceSystem}
								</Typography>
							</Box>
						</Box>

						<Box display="flex" flexWrap="nowrap" gap={1}>
							<Box flex="0 0 50%" className={classes.transferItemCol1}>
								<Typography variant="caption" color="textSecondary" component="div">
									{displayMode === "SearchResult" && "To"}
									{displayMode === "TransferList" && "Date Created"}
									{displayMode === "ReceiveList" && "Receive Created Date"}
								</Typography>
								<Typography variant="body2" component="div">
									{displayMode === "SearchResult" && (
										<span>
											{toLocation.locationId}
											{toLocation.locationName && ` - ${toLocation.locationName}`}
										</span>
									)}
									{displayMode === "TransferList" && getTimestampInLocalTime(dateCreated)}
									{displayMode === "ReceiveList" && receiveDateCreated}
								</Typography>
							</Box>
							<Box flex="0 0 50%" className={classes.transferItemCol2}>
								<Typography variant="caption" color="textSecondary" component="div">
									Transfer Type
								</Typography>
								<Typography variant="body2" component="div">
									{type}
								</Typography>
							</Box>
						</Box>

						<Box display="flex" flexWrap="nowrap" gap={1}>
							<Box flex="0 0 50%" className={classes.transferItemCol1}>
								<Typography variant="caption" color="textSecondary" component="div">
									{displayMode === "SearchResult" && "From"}
									{displayMode === "TransferList" && "Created By"}
									{displayMode === "ReceiveList" && "Receive Created By"}
								</Typography>
								<Typography variant="body2" component="div">
									{displayMode === "SearchResult" && (
										<span>
											{fromLocation.locationId}
											{fromLocation.locationName && ` - ${fromLocation.locationName}`}
										</span>
									)}
									{displayMode === "TransferList" && `${requesterName} (${requesterId})`}
									{displayMode === "ReceiveList" && receiveCreatedBy}
								</Typography>
							</Box>
							<Box flex="0 0 50%" className={classes.transferItemCol2}>
								<Typography variant="caption" color="textSecondary" component="div">
									Transfer Reason
								</Typography>
								<Typography variant="body2" component="div">
									{reason}
								</Typography>
							</Box>
						</Box>

						{(displayMode === "TransferList" || displayMode === "ReceiveList") && (
							<Box display="flex" flexWrap="nowrap" gap={1}>
								<Box flex="0 0 50%" className={classes.transferItemCol1}>
									<Typography variant="caption" color="textSecondary" component="div">
										From
									</Typography>
									<Typography variant="body2" component="div">
										<span>
											{fromLocation.locationId}
											{fromLocation.locationName && ` - ${fromLocation.locationName}`}
										</span>
									</Typography>
								</Box>
								<Box flex="0 0 50%" className={classes.transferItemCol2}>
									<Typography variant="caption" color="textSecondary" component="div">
										To
									</Typography>
									<Typography variant="body2" component="div">
										<span>
											{toLocation.locationId}
											{toLocation.locationName && ` - ${toLocation.locationName}`}
										</span>
									</Typography>
								</Box>
							</Box>
						)}

						{(consignmentCarrier || consignmentNumber) && (
							<Box display="flex" flexWrap="nowrap" gap={1}>
								{consignmentCarrier && (
									<Box flex="0 0 50%" className={classes.transferItemCol1}>
										<Typography variant="caption" color="textSecondary" component="div">
											Consignment Carrier
										</Typography>
										<Typography variant="body2" component="div">
											{consignmentCarrier}
										</Typography>
									</Box>
								)}
								{consignmentNumber && (
									<Box flexGrow={1} className={classes.transferItemCol2}>
										<Typography variant="caption" color="textSecondary" component="div">
											Consignment Number
										</Typography>
										<Box display="flex" alignItems="center">
											<Typography variant="body2" component="div">
												{consignmentNumber}
											</Typography>
											<CopyToClipboard text={consignmentNumber} />
										</Box>
									</Box>
								)}
							</Box>
						)}

						<Box>
							<SearchResultLink
								key={transfer.id}
								receiveId={id}
								receiveType={receiveType.transfer}
								searchDataSource={"Transfer"}
								transferLabelId={labelId}
								totalQuantity={totalApprovedQuantity === 0 ? totalInTransitQuantity : totalApprovedQuantity}
								totalCostInc={0}
								status={receiveStatus}
								onSelect={onSelect}
								highlight={false}
							/>
						</Box>
					</Stack>
				</CardContent>
			</Card>
		</>
	);
};

export default TransferCard;
